import { useCallback, useEffect, useRef, useState } from 'react';
import { getSettings, updateSettings, type SettingPatch } from '../../api/settings.ts';
import { realtimeClient } from '../../api/realtime.ts';
import { getStageChannel } from './stageChannel.ts';
import type { AppSetting, OpsSettings } from '../../api/types.ts';
import { mergeOpsSettings, DEFAULT_OPS_SETTINGS } from './opsSettings.ts';
import { apiErrorMessage } from '../../api/errorMessage.ts';
import { useT } from '../../i18n/i18n.tsx';

/**
 * Backs the Settings screen (AC-OPS-10: "every setting persisted to Strapi immediately with no
 * Save button"). Every `patch*` call here writes through to `PUT /api/setting` as soon as it is
 * called — there is no local-only draft state to lose on a refresh.
 *
 * `setting.ops` is a single opaque JSON column (see `opsSettings.ts`'s own doc comment): Strapi
 * replaces it wholesale on write, it does not deep-merge sub-keys server-side. `patchOps` is
 * therefore the ONLY sanctioned way to change one ops setting — it always computes and sends the
 * full merged tree, never a bare `{ marquee: {...} }` that would silently erase `attract`/
 * `overlays`/`hotkeys` on the next read.
 */
export function useOpsSettings() {
  const t = useT();
  const [setting, setSetting] = useState<AppSetting | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(0);
  const settingRef = useRef<AppSetting | null>(null);
  settingRef.current = setting;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fresh = await getSettings();
      setSetting(fresh);
      setLoadError(null);
    } catch (err) {
      // MVP-344: rendered verbatim at `SettingsScreen.tsx`'s `description={loadError}` — see
      // `api/errorMessage.ts`.
      setLoadError(apiErrorMessage(err, t('common.serverUnreachableRetry')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  // MVP-11 #3 — `setting.content-types.setting.lifecycles.js`'s `afterUpdate` publishes a
  // 'settings'/'setting.updated' SSE frame on every write (mirrors the session lifecycle's own
  // realtime pattern), but nothing here ever listened for it: each `useOpsSettings()` instance
  // (OperatorConsole, StageRoot, this shell's own Settings screen) only ever saw its OWN writes —
  // a remap or ticker change made from one mounted instance never reached any other tab/window
  // until it happened to reload. The published payload is a deliberately partial projection
  // (documentId/theme/stageTheme/venueName/ops — see that lifecycle's own comment), so this
  // re-fetches the full row via the existing `load()` rather than merging a partial shape over
  // `setting` and silently dropping the untouched fields (rotationMode, songsPerRotation, etc).
  // MVP-218 fix: the session lifecycle ALSO publishes on the `settings` channel (`session.updated`,
  // see `session/lifecycles.js`) so a session PATCH used to trip this too, firing a redundant
  // `GET /api/setting` on every rotation/message edit. Filtering to this hook's own event narrows
  // it to actual `setting` writes.
  useEffect(() => {
    return realtimeClient.onFrame((frame) => {
      if (frame.channel === 'settings' && frame.event === 'setting.updated') void load();
    });
  }, [load]);

  // MVP-11 #8 — the Stage window runs without the SSE client on purpose (App.tsx), so the frame
  // above never reaches it. Same-machine BroadcastChannel nudge instead; the Stage re-reads the
  // row itself. Harmless in the cockpit (it just re-fetches what it already wrote).
  useEffect(() => {
    return getStageChannel().onMessage((message) => {
      if (message.type === 'settings-updated') void load();
    });
  }, [load]);

  const patch = useCallback(async (partial: SettingPatch): Promise<AppSetting | null> => {
    inFlight.current += 1;
    setSaving(true);
    try {
      const updated = await updateSettings(partial);
      setSetting(updated);
      setSaveError(null);
      getStageChannel().post({ type: 'settings-updated', sentAtMs: Date.now() });
      return updated;
    } catch (err) {
      // MVP-344: rendered verbatim at `SettingsScreen.tsx:163`. `settings.session.saveError` is the
      // same sentence MVP-337 chose for the neighbouring Tonight save — one failed write, one line.
      setSaveError(apiErrorMessage(err, t('settings.session.saveError')));
      return null;
    } finally {
      inFlight.current -= 1;
      if (inFlight.current === 0) setSaving(false);
    }
  }, [t]);

  const ops: OpsSettings = mergeOpsSettings(setting?.ops);

  const patchOps = useCallback(
    (partialOps: Partial<OpsSettings>) => {
      const base = mergeOpsSettings(settingRef.current?.ops);
      const merged: OpsSettings = {
        marquee: { ...base.marquee, ...partialOps.marquee },
        attract: { ...base.attract, ...partialOps.attract },
        overlays: { ...base.overlays, ...partialOps.overlays },
        hotkeys: { ...base.hotkeys, ...partialOps.hotkeys },
        autoDuck: { ...base.autoDuck, ...partialOps.autoDuck },
      };
      return patch({ ops: merged });
    },
    [patch]
  );

  /** AC-OPS-10's "an explicit reset this section" for the Stage group — restores exactly the
   * documented defaults, still going through the same immediate-write path (never a local-only
   * revert the next reload would undo). */
  const resetOpsSection = useCallback(
    (section: keyof OpsSettings) => {
      return patchOps({ [section]: DEFAULT_OPS_SETTINGS[section] } as Partial<OpsSettings>);
    },
    [patchOps]
  );

  return { setting, ops, loading, loadError, saving, saveError, refresh: load, patch, patchOps, resetOpsSection };
}
